import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { compose } from 'redux'
import { dataTableActions, dataTableValueSelectors, getStyles } from 'isotope-client'
import { makeStyles } from '@material-ui/styles'
import { Grid, IconButton } from '@material-ui/core'
import moment from 'moment'
import { PLANIFICATION_TABLE_NAME } from '../../utils/constants'
import Selected from '@material-ui/icons/LibraryAddCheck'
import classNames from 'classnames'
import { FormattedMessage } from 'react-intl'
import { sortObjectByLabel } from '../../../../../utils/utils'
import { useGestionTacheContext } from '../../GestionTacheProvider'
import ToolBarButton from '../../../planning/component/ToolBar/ToolBarButton'
import FilterIcon from '../../../../../components/icon/FilterIcon'
import { injectPlanificationFilter } from '../../injectors/filterInjector'
import Button from '../../../../../components/Button'
import PlaylistAddIcon from '@material-ui/icons/PlaylistAdd'
import ButtonWithMenu from '../../../../../components/ButtonWithMenu'
import MoreHorizIcon from '@material-ui/icons/MoreHoriz'
import { DeleteForever } from '@material-ui/icons'
import { DialogContext } from '../../../../../components/layout/dialog'
import { useSnackbar } from '../../../../../components/layout/snackbar/SnackbarContext'
import { STATUTS_TACHE, TYPE_DIALOG } from '../../../../../utils/constants'
import ContenuPopinSuppressionErrorTache from './ContenuPopinSuppressionErrorTache'
import ContenuPopinSuppressionTache, { formName } from './ContenuPopinSuppressionTache'
import FormButton from '../../../../../components/form/FormButton'
import { addInstructions, delayTache, deleteTasks as deleteTasksApi, getAllUsers } from '../../gestionTachesApi'
import WarningRoundedIcon from '@material-ui/icons/WarningRounded'
import InstructionForm from '../../../tourPlaine/component/panel/InstructionForm'
import { ACTIONS_KEY, ADD_INSTRUCTION_FIELD, MOVE_TASK_FIELD, PANEL_FORM } from '../../../tourPlaine/tool/tourPlaine.constants'
import { FilterPanelContext } from '../../../../../components/layout/rightPanels'
import NoteAddIcon from '@material-ui/icons/NoteAdd'
import SyncAltIcon from '@material-ui/icons/SyncAlt'
import MoveForm from '../../../tourPlaine/component/panel/MoveForm'
import { injectCreateTask } from '../../injectors/createTaskInjector'
import QuestionIcon from '../../../../../components/icon/QuestionIcon'
import LegendeTaches from '../LegendeTaches'
import { injectAffectation } from '../../injectors/affectationInjector'
import { LocalDate } from '../../../../../utils/date/local-date'
import { DateFormat } from '../../../../../utils/date/model/date'
import TransferWithinAStationIcon from '@material-ui/icons/TransferWithinAStation'

const useStyles = makeStyles(theme => getStyles({
	root: {
		marginBottom: 8,
		height: 50,
		fontFamily: 'Lato',
		maxWidth: '100%'
	},
	cell: {
		backgroundColor: theme.palette.primary.light
	},
	selectedTaches: {
		width: 'calc(37.4%)'
	},
	iconContainer: {
		width: 60
	},
	icon: {
		fill: theme.palette.primary.main
	},
	kpiContainer: {
		width: 'unset',
		paddingLeft: 5
	},
	tachesSelectionneesDuree: {
		fontWeight: 'bold',
		fontSize: 16
	},
	label: {
		fontSize: 13
	},
	title: {
		fontStyle: 'italic',
		fontSize: 14,
		alignContent: 'center'
	},
	bouton: {
		padding: '5px 26px',
		marginLeft: 20
	}
}))

const HeaderPlanification = ({ selected, setFilterOpen, resetSelectedRows, openCreateTask, affecterDesaffecterTache, refreshTable }) => {
	const classes = useStyles()
	const { jourSelection } = useGestionTacheContext()
	const { isResultat } = useGestionTacheContext()
	const { openDialog, closeDialog } = React.useContext(DialogContext)
	const { snackError, snackSuccess } = useSnackbar()
	const { openPanel, closePanel } = React.useContext(FilterPanelContext)
	const [users, setUsers] = React.useState([])

	React.useEffect(() => {
		getAllUsers(LocalDate.fromString(jourSelection).format(DateFormat.SHORT_DATE_WITH_DASH))
			.then(users => setUsers(sortObjectByLabel(users.map((ressource) => ({
				label: ressource.alias,
				code: ressource.user.id,
				value: ressource.user.id,
				alias: ressource.alias
			})))))
	}, [])

	const chargeTachesSelectionnees = React.useMemo(
		() => selected.reduce((acc, tache) => acc.add(moment.duration(tache.chargeTravail)), moment.duration(0)),
		[selected]
	)

	const onActionSuccess = () => {
		closePanel()
		resetSelectedRows()
		refreshTable()
	}

	const moveTasks = () => {
		openPanel(
			<Grid container alignItems="center" style={{ gap: 20 }}>
				<SyncAltIcon color="primary" />
				<FormattedMessage id="gestionTaches.ecranPlanification.move.title" values={{ quantity: selected.length }} />
			</Grid>,
			<MoveForm
				isFromPlanification
				items={selected}
				type={ACTIONS_KEY.MOVE_TASK}
				onSubmit={values => delayTache(selected.map(t => t.id), values[MOVE_TASK_FIELD.DATE], null)
					.then(() => {
						onActionSuccess()
						snackSuccess({ id: 'listAssolement.snackbar.ok.deplacerEtapes' })
					})
					.catch(() => snackError({ id: 'listAssolement.snackbar.error.deplacerEtapes' }))}
			/>,
			[
				<Button type="secondary" onClick={closePanel}>
					<FormattedMessage id="actions.cancel" />
				</Button>,
				<FormButton type="primary" formName={PANEL_FORM.MOVE_TASK}>
					<FormattedMessage id="tourPlaine.form.submit" />
				</FormButton>
			]
		)
	}

	const ajouterInstructions = () => {
		openPanel(
			<Grid container alignItems="center" style={{ gap: 20 }}>
				<NoteAddIcon color="primary" />
				<FormattedMessage id="tourPlaine.form.instruction.title" />
			</Grid>,
			<InstructionForm
				isFromPlanification
				items={selected}
				onSubmit={values => addInstructions(selected.map(t => t.id), values[ADD_INSTRUCTION_FIELD.INSTRUCTION])
					.then(() => {
						onActionSuccess()
						snackSuccess({ id: 'snackbar.update.tourPlaine.generic' })
					})
					.catch(() => snackError({ id: 'snackbar.error.tourPlaine.generic' }))
				}
			/>,
			[<FormButton type="primary" formName={PANEL_FORM.ADD_INSTRUCTION}>
				<FormattedMessage id="tourPlaine.form.submit" />
			</FormButton>]
		)
	}

	const deleteTasks = () => {
		if (selected.some(task => task.statut === STATUTS_TACHE.TERMINEE)) {
			openDialog(
				<span className={classes.spanTitlePopin}>
					<WarningRoundedIcon className={classes.error} />
					<FormattedMessage id="gestionTaches.toolbar.actions.delete.popin.error.title" />
				</span>,
				<ContenuPopinSuppressionErrorTache tachesEnErreur={selected.filter(tache => tache.statut === STATUTS_TACHE.TERMINEE)} />,
				[], TYPE_DIALOG.ACTION, 'actions.close'
			)
		} else {
			openDialog(
				{ id: 'gestionTaches.toolbar.actions.delete.popin.validation.title' },
				<ContenuPopinSuppressionTache tachesRecurrentes={selected.filter(tache => tache.recurrence)} onSubmit={handleDeleteTasks} />,
				[
					<FormButton
						type="primary"
						formName={formName}
					>
						<FormattedMessage id="actions.delete" />
					</FormButton>
				], TYPE_DIALOG.ACTION
			)
		}
	}

	const handleDeleteTasks = React.useCallback((values) => {
		deleteTasksApi(selected.map(task => task.id), values.typeSuppression).then(() => {
			onActionSuccess()
			snackSuccess({ id: 'gestionTaches.toolbar.actions.delete.success' })
		}).catch(() => snackError({ id: 'gestionTaches.toolbar.actions.delete.error' }))
	}, [selected])

	return <Grid container className={classes.root}>
		<Grid container item xs={6}>
			<Grid item container className={classNames(classes.cell, classes.selectedTaches)} alignItems="center" wrap="nowrap">
				<Grid item container className={classes.iconContainer} alignContent="center" justify="center">
					<Selected className={classes.icon} />
				</Grid>
				<Grid item container direction="column" className={classes.kpiContainer}>
					<span className={classes.tachesSelectionneesDuree}>{chargeTachesSelectionnees.format('h[h]mm', { trim: false })}</span>
					<FormattedMessage id="gestionTaches.ecranPlanification.header.tachesSelectionnees">
						{text => <span className={classes.label}>{text}</span>}
					</FormattedMessage>
				</Grid>
			</Grid>
		</Grid>
		<Grid container item xs={6} justify="space-around" direction="row">
			<Grid container item justify="flex-end" alignItems="center" direction="row">
				{!isResultat && <>
					<Button
						type="primary"
						startIcon={<PlaylistAddIcon />}

						onClick={() => openCreateTask(onActionSuccess)}
						className={classes.bouton}
					>
						<FormattedMessage id="gestionTaches.ecranPlanification.ajout.title" />
					</Button>
					<ButtonWithMenu
						type="secondary"
						startIcon={<MoreHorizIcon />}
						items={[
							{
								label: <FormattedMessage id="gestionTaches.ecranPlanification.header.actions.move" values={{ quantity: selected.length }} />,
								icon: <SyncAltIcon color="primary" />,
								onClick: () => moveTasks()
							},
							{
								label: <FormattedMessage id="gestionTaches.ecranPlanification.header.actions.updateAffectation" />,
								icon: <TransferWithinAStationIcon color="primary" />,
								onClick: () => affecterDesaffecterTache(jourSelection, selected, users, onActionSuccess)
							},
							{
								label: <FormattedMessage id="gestionTaches.ecranPlanification.header.actions.addInstruction" />,
								icon: <NoteAddIcon color="primary" />,
								onClick: () => ajouterInstructions()
							},
							{
								label: <FormattedMessage id="gestionTaches.ecranPlanification.header.actions.delete" values={{ quantity: selected.length }} />,
								icon: <DeleteForever color="primary" />,
								onClick: () => deleteTasks()
							}
						]}
						disabled={!selected.length}
						className={classes.bouton}
					>
						<FormattedMessage id="actions.other" />
					</ButtonWithMenu>
				</>}
				<ToolBarButton type="filter" onClick={setFilterOpen}>
					<FilterIcon color="currentColor" />
				</ToolBarButton>
				<IconButton className={classes.icon} onClick={() => openDialog({ id: 'legende.title' }, <LegendeTaches />, [], TYPE_DIALOG.DETAILS, 'actions.close')}>
					<QuestionIcon />
				</IconButton>
			</Grid>
		</Grid>
	</Grid>

}

HeaderPlanification.propTypes = {
	classes: PropTypes.object
}

const mapStateToProps = (state) => ({
	selected: dataTableValueSelectors.getDataTableSelectedRows(state)(PLANIFICATION_TABLE_NAME) || []
})

const actions = {
	refreshTable: () => dataTableActions.refresh(PLANIFICATION_TABLE_NAME, []),
	resetSelectedRows: () => dataTableActions.selectRows(PLANIFICATION_TABLE_NAME, [])
}

export default compose(
	connect(mapStateToProps, actions),
	injectPlanificationFilter,
	injectCreateTask,
	injectAffectation
)(HeaderPlanification)
